import React, { useState } from "react";
import {
    Typography,
    FormControl,
    Select,
    MenuItem,
    Box,
    IconButton,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import theme from "../../../components/LayoutComponents/theme";
import { deleteMatchRequest } from "../../../remote/usersRequests";

function SkillRow({ skill, session, setSkills, proficiencies, handleProficiencyChange, newlyAddedSkills, setLoading, isMobile, showToast }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
    const menuOpen = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewSource = () => {
        setSourceDialogOpen(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        handleMenuClose();
        if (
            window.confirm(
                'Are you sure you want to delete this match? This action cannot be undone.'
            )) {
            try {
                setLoading(true);
                let res = await deleteMatchRequest(session?.token, Number(skill?.compXwalkId));
                if (res.data) {
                    showToast?.('Match deleted successfully', 'success');
                    setSkills(res.data || []);
                }
            } catch (e) {
                showToast?.('Delete match failed, try again.', 'error');
            } finally {
                setLoading(false);
            }
        }
    };

    if (!skill) return null;

    const isNewlyAdded = newlyAddedSkills.includes(skill.compXwalkId);

    return (
        <>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr auto auto" : "1fr 200px 56px",
                    alignItems: "center",
                    gap: 2,
                    padding: "12px 16px",
                    borderBottom: "1px solid #e0e0e0",
                    backgroundColor: isNewlyAdded ? "rgba(76, 175, 80, 0.08)" : "#fff",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                        backgroundColor: isNewlyAdded ? "rgba(76, 175, 80, 0.12)" : "#f5f5f5",
                    },
                }}
            >
                {/* Column 1: Skill Name */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 500,
                            fontSize: isMobile ? "0.875rem" : "0.95rem",
                            color: "#333",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                        }}
                    >
                        {skill.competencyDescription}
                    </Typography>
                    {skill.modified && (
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: theme.colors.purple,
                                flexShrink: 0,
                            }}
                            title="Modified"
                        />
                    )}
                </Box>

                {/* Column 2: Proficiency Dropdown */}
                <FormControl size="small" sx={{ minWidth: isMobile ? 100 : 180 }}>
                    <Select
                        value={skill.proficiencyRating ?? 1}
                        onChange={(e) =>
                            handleProficiencyChange(
                                skill.compXwalkId,
                                skill.competencyId,
                                parseInt(e.target.value, 10)
                            )
                        }
                        sx={{
                            fontSize: "0.875rem",
                            "& .MuiSelect-select": {
                                padding: "8px 12px",
                            },
                        }}
                    >
                        {proficiencies.map(({ profId, profRating, profDescription }) => (
                            <MenuItem
                                key={profId}
                                value={profRating}
                                sx={{
                                    fontSize: "0.875rem",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                }}
                            >
                                {profDescription}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Column 3: Kebab Menu */}
                <IconButton
                    onClick={handleMenuClick}
                    size="small"
                    sx={{
                        color: "#666",
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.04)",
                            color: theme.colors.purple,
                        },
                    }}
                >
                    <MoreVertIcon />
                </IconButton>
            </Box>

            {/* Kebab Menu */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                    elevation: 3,
                    sx: {
                        borderRadius: 2,
                        minWidth: 160,
                        "& .MuiMenuItem-root": {
                            fontSize: "0.875rem",
                            gap: 1.5,
                            py: 1,
                        },
                    },
                }}
            >
                <MenuItem onClick={handleViewSource}>
                    <VisibilityIcon fontSize="small" sx={{ color: "#666" }} />
                    View Source
                </MenuItem>
                <MenuItem onClick={handleDelete} sx={{ color: "#d32f2f" }}>
                    <DeleteIcon fontSize="small" />
                    Delete
                </MenuItem>
            </Menu>

            {/* Source Dialog */}
            <Dialog
                open={sourceDialogOpen}
                onClose={() => setSourceDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 },
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: theme.colors.purple }}>
                    Source Statement
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#555", lineHeight: 1.6 }}>
                        {skill.originalStatement || "No source statement provided."}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setSourceDialogOpen(false)}
                        variant="contained"
                        sx={{
                            backgroundColor: theme.colors.purple,
                            "&:hover": { backgroundColor: theme.colors.purple, opacity: 0.9 },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default React.memo(SkillRow);
